/**
 * Vendors List Page
 * Organization-level vendor management
 * BuilderBel Construction Project Management System
 */
import { Building2, ChevronLeft, ChevronRight, Inbox, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { ActionMenu, EmptyState, ModuleLayout, StatusBadge } from '../components/ui';
import { VendorDrawer } from '../components/vendors';
import { useToast } from '../contexts/ToastContext';
import {
  useDeleteVendorMutation,
  useGetVendorsQuery,
  useGetVendorStatsQuery,
} from '../services/vendorApi';
import type { Vendor, VendorStatus, VendorType } from '../types/vendor';

// Status filter options
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Type filter options
const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'equipment', label: 'Equipment Rental' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'utility', label: 'Utility Provider' },
  { value: 'other', label: 'Other' },
];

// Sort options
const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'code', label: 'Code' },
  { value: 'type', label: 'Type' },
  { value: 'createdAt', label: 'Created Date' },
];

// Get status variant for StatusBadge
const getStatusVariant = (status: VendorStatus): 'success' | 'neutral' => {
  return status === 'active' ? 'success' : 'neutral';
};

// Get type label
const getTypeLabel = (type: VendorType): string => {
  const labels: Record<VendorType, string> = {
    supplier: 'Supplier',
    subcontractor: 'Subcontractor',
    equipment: 'Equipment',
    professional: 'Professional',
    utility: 'Utility',
    other: 'Other',
  };
  return labels[type] || type;
};

// Get type color
const getTypeColor = (type: VendorType): string => {
  const colors: Record<VendorType, string> = {
    supplier: 'bg-blue-100 text-blue-700',
    subcontractor: 'bg-amber-100 text-amber-700',
    equipment: 'bg-purple-100 text-purple-700',
    professional: 'bg-cyan-100 text-cyan-700',
    utility: 'bg-pink-100 text-pink-700',
    other: 'bg-gray-100 text-gray-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-700';
};

const Vendors: React.FC = () => {
  const toast = useToast();

  // State for filters and pagination
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<VendorStatus | ''>('');
  const [type, setType] = useState<VendorType | ''>('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // State for drawer
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');

  // State for dropdown menu
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API calls
  const {
    data: vendorsResponse,
    isLoading,
    refetch,
  } = useGetVendorsQuery({
    page,
    limit,
    search: search || undefined,
    status: status || undefined,
    type: type || undefined,
    sortBy: sortBy as 'name' | 'code' | 'type' | 'createdAt',
    sortOrder,
  });

  const { data: statsResponse } = useGetVendorStatsQuery();
  const [deleteVendor, { isLoading: isDeleting }] = useDeleteVendorMutation();

  // Extract data
  const vendors = vendorsResponse?.data?.vendors || [];
  const pagination = vendorsResponse?.data?.pagination;
  const stats = statsResponse?.data;

  // Handlers
  const handleCreateNew = useCallback(() => {
    setSelectedVendor(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((vendor: Vendor) => {
    setSelectedVendor(vendor);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
    setActiveMenuId(null);
  }, []);

  const handleDelete = useCallback(
    async (vendor: Vendor) => {
      if (!window.confirm(`Are you sure you want to delete "${vendor.name}"?`)) {
        return;
      }

      try {
        const result = await deleteVendor(vendor._id).unwrap();
        if (result.success) {
          toast.success('Vendor deleted successfully');
        } else {
          toast.error(result.message || 'Failed to delete vendor');
        }
      } catch (error: any) {
        toast.error(error.data?.message || 'Failed to delete vendor');
      }
      setActiveMenuId(null);
    },
    [deleteVendor, toast]
  );

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedVendor(null);
  }, []);

  const handleDrawerSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Pagination handlers
  const handlePrevPage = useCallback(() => {
    if (page > 1) setPage(page - 1);
  }, [page]);

  const handleNextPage = useCallback(() => {
    if (pagination && page < pagination.totalPages) setPage(page + 1);
  }, [page, pagination]);

  // Stats cards
  const statsCards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: 'Total Vendors', value: stats.total, color: 'text-gray-900' },
      { label: 'Active', value: stats.active, color: 'text-green-600' },
      { label: 'Inactive', value: stats.inactive, color: 'text-gray-500' },
    ];
  }, [stats]);

  // Loading state
  if (isLoading) {
    return (
      <ModuleLayout
        title="Vendors"
        description="Manage your organization's vendors and suppliers"
        icon={<Building2 className="w-5 h-5 text-brand-600" />}
      >
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout
      title="Vendors"
      description="Manage your organization's vendors and suppliers"
      icon={<Building2 className="w-5 h-5 text-brand-600" />}
      primaryAction={{
        label: 'New Vendor',
        onClick: handleCreateNew,
        icon: <Plus className="w-3 h-3 mr-1" />,
      }}
    >
      {/* Stats */}
      {statsCards.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {statsCards.map(stat => (
            <div key={stat.label} className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className={`text-2xl font-semibold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search vendors..."
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          {/* Status Filter */}
          <div className="w-40">
            <SearchableDropdown
              options={STATUS_OPTIONS}
              value={status}
              onChange={value => {
                setStatus(value as VendorStatus | '');
                setPage(1);
              }}
              placeholder="Status"
            />
          </div>

          {/* Type Filter */}
          <div className="w-48">
            <SearchableDropdown
              options={TYPE_OPTIONS}
              value={type}
              onChange={value => {
                setType(value as VendorType | '');
                setPage(1);
              }}
              placeholder="Type"
            />
          </div>

          {/* Sort */}
          <div className="w-40">
            <SearchableDropdown
              options={SORT_OPTIONS}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
            />
          </div>

          {/* Sort Order */}
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {vendors.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No vendors found"
            description={
              search || status || type
                ? 'No vendors match your current filters'
                : 'Get started by adding your first vendor'
            }
            action={
              !search && !status && !type
                ? {
                    label: 'Add Vendor',
                    onClick: handleCreateNew,
                  }
                : undefined
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vendors.map(vendor => (
                    <tr
                      key={vendor._id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleEdit(vendor)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{vendor.code}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{vendor.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(vendor.type)}`}
                        >
                          {getTypeLabel(vendor.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {vendor.contact?.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {vendor.contact?.email || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {vendor.contact?.phone || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={vendor.status}
                          variant={getStatusVariant(vendor.status)}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div onClick={e => e.stopPropagation()} className="inline-block">
                          <ActionMenu
                            id={vendor._id}
                            isOpen={activeMenuId === vendor._id}
                            onOpenChange={open => setActiveMenuId(open ? vendor._id : null)}
                            items={[
                              {
                                label: 'Edit',
                                icon: <Pencil className="w-3.5 h-3.5" />,
                                onClick: () => handleEdit(vendor),
                              },
                              {
                                label: 'Delete',
                                icon: <Trash2 className="w-3.5 h-3.5" />,
                                onClick: () => handleDelete(vendor),
                                variant: 'danger',
                                disabled: isDeleting,
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, pagination.total)} of{' '}
                  {pagination.total} vendors
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={page === 1}
                    className="p-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={page >= pagination.totalPages}
                    className="p-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Vendor Drawer */}
      <VendorDrawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        vendor={selectedVendor}
        mode={drawerMode}
        onSuccess={handleDrawerSuccess}
      />
    </ModuleLayout>
  );
};

export default Vendors;
